import { useCallback } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useUIStore } from '@/shared/stores/ui-store';
import { scrollToFirstError } from '../../logic/scroll-to-first-error';

/** Depth-first search for the first RHF validation message in an errors tree. */
function firstErrorMessage(errors: FieldErrors): string | undefined {
  for (const value of Object.values(errors)) {
    if (!value || typeof value !== 'object') continue;
    const message = (value as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
    const nested = firstErrorMessage(value as FieldErrors);
    if (nested) return nested;
  }
  return undefined;
}

/**
 * Returns an RHF `onInvalid` handler that surfaces the first validation error
 * as a toast.
 *
 * Wizard steps submit via `handleSubmit(onSubmit)`. When zod validation fails,
 * `onSubmit` (and therefore `onNext`) never runs — so a "Continue" click does
 * nothing and shows no error, which reads as a dead button. Passing this as the
 * second `handleSubmit` argument makes the blocking reason visible
 * ("Please select an ad account", "Daily budget is below the minimum…", etc.),
 * including for fields whose inline error has nowhere to render.
 */
export function useWizardInvalidToast(): (errors: FieldErrors) => void {
  const addToast = useUIStore((s) => s.addToast);
  return useCallback(
    (errors: FieldErrors) => {
      addToast({
        variant: 'error',
        message:
          firstErrorMessage(errors) ?? 'Please complete the required fields before continuing.',
      });
      scrollToFirstError();
    },
    [addToast],
  );
}
